import React from 'react';
import './AlertSteps.css';
import { PREVIEW_ROW_CAP } from '../useAlertIrAuthor';
import { capDisplayDecimals } from '../../../../utils/numberFormat';

/**
 * The result the alert's own query returned.
 *
 * **Why this exists at all.** The shared Report query step's preview is fed by
 * REGENERATE, which runs the chat query — a different definition of the alert
 * from the one it saves. On the alerts variant that left the author picking
 * subject, measure and period columns out of a result they had never seen,
 * under a panel still reading "Run or select a query to see preview". The two
 * definitions this epic exists to collapse had quietly reappeared one layer up,
 * in the preview. Raised in review of #177, against a live stack.
 *
 * So the alerts step shows *this* instead: the rows the authored query
 * returned, which are the rows the comparison below reads its columns from.
 *
 * **The row count lives here and nowhere else.** There are two honest counts on
 * this step and they can legitimately differ — the query's rows, and the
 * subjects the comparison matches. Naming both "count" beside each other is how
 * an author calibrates a threshold against the wrong number. The query's count
 * is stated here, in the panel showing the rows it counts; the matched-subject
 * count stays in the live-count panel below, which says "subjects match".
 *
 * `matchCount` is the far side's *uncapped* total, which is the point of it
 * being sent separately — `rows` is capped at `PREVIEW_ROW_CAP` for display.
 */
/**
 * What the query assumed in place of a value from the question that the data does not
 * hold, one sentence each, written by the service. Shown whether or not any rows matched:
 * an alert built on "I took 'X' to mean 'Y'" should not be saved without the author seeing it.
 */
export const AlertSubstitutions = ({ substitutions = [] }) => {
  const lines = (Array.isArray(substitutions) ? substitutions : []).filter(Boolean);
  if (!lines.length) return null;
  return (
    <ul className="alert-preview-substitutions">
      {lines.map((line, i) => <li key={`${i}-${line}`}>{line}</li>)}
    </ul>
  );
};

const AlertQueryPreview = ({
  sql, columns = [], rows = [], matchCount = 0, loading = false, substitutions = [],
}) => {
  if (loading) {
    return <span className="alert-preview-empty">Building the query…</span>;
  }

  if (!sql) {
    return (
      <span className="alert-preview-empty">
        Build the comparison from your question to see the rows this alert will run against.
      </span>
    );
  }

  const names = columns.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean);
  const shown = rows.slice(0, PREVIEW_ROW_CAP);
  // `matchCount` is uncapped and `rows` is not, so the total is whichever is
  // larger — a far side that stops sending the count must not make the preview
  // claim fewer rows than it is visibly displaying.
  const total = Math.max(matchCount, rows.length);

  if (names.length === 0 || shown.length === 0) {
    return (
      <>
        <AlertSubstitutions substitutions={substitutions} />
        <span className="alert-preview-empty">
          The query ran and returned no rows. The comparison below can still be
          built, but nothing matches it today.
        </span>
      </>
    );
  }

  return (
    <div className="alert-preview">
      <AlertSubstitutions substitutions={substitutions} />
      <p className="alert-preview-caption">
        {total} row{total === 1 ? '' : 's'} returned by this alert&apos;s query
        {total > shown.length ? `, first ${shown.length} shown` : ''}.
      </p>
      <div className="alert-preview-scroll">
        <table className="alert-preview-table">
          <thead>
            <tr>
              {/* The bare column name, not `columnLabel`'s "description
                  (name)" — that form reads well in a select where the author is
                  choosing, and badly as a table header repeated down a result.
                  The description is kept one hover away instead. */}
              {names.map((name, i) => (
                <th key={name} scope="col" title={describe(columns[i])}>{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Keyed by index: result rows carry no identity of their own and
                may legitimately repeat, so any content-derived key collides. */}
            {shown.map((row, i) => (
              <tr key={i}>
                {names.map((name) => (
                  <td key={name}>{formatCell(row?.[name])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const describe = (column) => (
  column && typeof column === 'object' ? column.description || undefined : undefined
);

/**
 * `null` is a result the query returned and an empty cell is a value it did
 * not; rendering both as nothing loses that. Objects are stringified rather
 * than dropped so an unexpected shape is visible instead of silently blank.
 *
 * A number is capped at two decimals, like every number on screen. Only by
 * type: the columns carry no types here, and a string that looks numeric may be
 * a code or a value the query passed through as written.
 */
const formatCell = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'number') return capDisplayDecimals(value);
  return String(value);
};

export default AlertQueryPreview;
